import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle } from "lucide-react";
import { preferencesValidator } from "shared";
import { toast } from "sonner";
import type { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAppForm } from "@/hooks/useCreateForm";
import { useRequiredRole } from "@/hooks/useRequiredRole";
import { queryClient, trpc } from "@/lib/trpcClient";

export const Route = createFileRoute("/_authed/_org/settings/notifications")({
  component: RouteComponent,
});

type PreferencesValidator = z.infer<typeof preferencesValidator>;

function RouteComponent() {
  const { data: preferences } = useSuspenseQuery(
    trpc.auth.getPreferences.queryOptions(),
  );

  const { mutate: setPreferences, isPending } = useMutation(
    trpc.auth.setPreferences.mutationOptions(),
  );

  const { bookkeeper } = useRequiredRole();

  const preferencesForm = useAppForm({
    defaultValues: preferences as PreferencesValidator,
    onSubmit: async ({ value }) => {
      await setPreferences(value);
      toast.success("Preferences updated");
      await queryClient.refetchQueries(trpc.auth.getPreferences.queryOptions());
      preferencesForm.reset();
    },
    onSubmitInvalid: (errors) => console.log(errors.formApi.getAllErrors()),
    validators: {
      onSubmit: preferencesValidator,
    },
  });

  return (
    <form
      className="flex flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        preferencesForm.handleSubmit(e);
      }}
    >
      <preferencesForm.Field
        name="incompleteTransactionReminders"
        children={(field) => (
          <EmailReminderPreference
            reminderSubject="Remind me about incomplete transactions"
            description="Receive emails reminding you to take care of bookkeeping for your transactions"
            handleChange={field.handleChange}
            fieldValue={field.state.value}
            withFrequency
          />
        )}
      />

      <div className="my-6 h-px w-full bg-border" />

      <preferencesForm.Field
        name="approvalNeededReminders"
        children={(field) => (
          <EmailReminderPreference
            reminderSubject="Remind me about transactions that need my approval"
            description="Receive emails reminding you to review transactions that include you in their approval policy"
            handleChange={field.handleChange}
            fieldValue={field.state.value}
            withFrequency
          />
        )}
      />

      <div className="my-6 h-px w-full bg-border" />

      {bookkeeper && (
        <preferencesForm.Field
          name="creditLimitReminders"
          children={(field) => (
            <EmailReminderPreference
              reminderSubject="Notify me when we cross 70% of our credit limit"
              description="Receive emails letting you know that you are approaching your credit limit. You can run a manual payment to clear your balance at any time."
              handleChange={field.handleChange}
              fieldValue={field.state.value}
            />
          )}
        />
      )}

      <preferencesForm.Subscribe selector={(state) => state.isDefaultValue}>
        {(isDefaultValue) =>
          !isDefaultValue && (
            <Button className="mt-12 w-40" size="sm" disabled={isPending}>
              <CheckCircle className="size-3.5" />
              Save Changes
            </Button>
          )
        }
      </preferencesForm.Subscribe>
    </form>
  );
}

type EmailPreferenceType =
  PreferencesValidator["incompleteTransactionReminders"];

type EmailReminderBaseProps<T> = {
  reminderSubject: string;
  description: string;
  handleChange?: T extends true
    ? (value: EmailPreferenceType) => void
    : (value: boolean) => void;
  fieldValue?:
    | (T extends true ? EmailPreferenceType : boolean)
    | null
    | undefined;
};

type EmailReminderProps =
  | ({
      withFrequency: true;
    } & EmailReminderBaseProps<true>)
  | ({
      withFrequency?: false;
    } & EmailReminderBaseProps<false>);

function EmailReminderPreference({
  reminderSubject,
  description,
  handleChange,
  fieldValue,
  withFrequency,
}: EmailReminderProps) {
  return (
    <div className="flex flex-col gap-8 md:flex-row md:items-center">
      <div className="flex max-w-3xl grow flex-col gap-1">
        <h1 className="font-medium text-lg text-neutral-800">
          {reminderSubject}
        </h1>

        <h4 className="max-w-xl text-neutral-500 text-xs">{description}</h4>
      </div>

      <div className="flex h-8 items-center gap-8">
        <Switch
          className="md:ml-auto"
          checked={Boolean(fieldValue)}
          onCheckedChange={(checked) => {
            if (withFrequency) handleChange?.(checked ? "daily" : null);
            else handleChange?.(checked);
          }}
        />

        {withFrequency && (
          <>
            <div className="h-full w-px bg-border" />

            <Select
              onValueChange={(value) => {
                if (value) handleChange?.(value as EmailPreferenceType);
              }}
              value={fieldValue ?? undefined}
              disabled={!fieldValue}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="other-days">Every other day</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
      </div>
    </div>
  );
}
